// Industry-level resource time-series for the Overview. Backed by ClickHouse
// (/finops/v2/.../resource-timeseries) so it spans the FULL selected window with
// adaptive bucketing — fixing the legacy /metrics path that returned only the last
// ~200 raw snapshots (≈2h) no matter what range was picked.
//
// x-axis is a NUMERIC epoch-ms scale pinned to [since_ms, now_ms] (the whole
// window), so the line always covers the selected range and the ticks adapt from
// hour labels (≤24h) to day labels (longer). Utilization (used ÷ capacity) is
// overlaid with Allocation (requested ÷ capacity) — the "are we using what we
// reserve" story every FinOps tool shows.

import React from 'react';
import { Card, CardContent } from '../ui/card';
import {
  LineChart, Line, ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Cpu, HardDrive, Server } from 'lucide-react';
import { useResourceTimeseries } from '../../hooks/useFinopsV2';
import { Skeleton, ChartLegend } from './finopsUi';
import { useChartTheme } from './chartTheme';

const TOOLTIP = {
  contentStyle: { backgroundColor: 'rgba(15,23,42,0.96)', border: 'none', borderRadius: 12,
    boxShadow: '0 20px 40px -12px rgba(0,0,0,0.5)', padding: '12px 14px', backdropFilter: 'blur(8px)' },
  labelStyle: { color: '#f1f5f9', fontWeight: 700, marginBottom: 8, fontSize: 12 },
  itemStyle: { color: '#e2e8f0', fontSize: 13, padding: '2px 0' },
};

function tickFmt(hours) {
  return (ms) => {
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    if (hours <= 24) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    if (hours <= 168) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
}
const labelFmt = (ms) => new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

function ChartCard({ icon: Icon, tint, title, legend, children }) {
  return (
    <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={`w-4 h-4 shrink-0 ${tint}`} />
            <h3 className="text-sm font-semibold text-slate-700 dark:text-white truncate">{title}</h3>
          </div>
          {legend}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function ResourceTimeseriesPanel({ clusterId, hours }) {
  const { data, loading, available } = useResourceTimeseries(clusterId, hours);
  const theme = useChartTheme();
  // Hidden entirely when ClickHouse is off → the page falls back to legacy charts.
  if (!available) return null;
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-72" />)}
      </div>
    );
  }
  const pts = data?.points || [];
  if (!pts.length) return null;
  const domain = [data.since_ms, data.now_ms];
  const fmt = tickFmt(hours);
  const xAxis = (
    <XAxis dataKey="t" type="number" scale="time" domain={domain} {...theme.axis}
           tickFormatter={fmt} minTickGap={44} allowDataOverflow />
  );
  const utilLegend = (color) => (
    <ChartLegend items={[
      { label: 'Utilization', color },
      { label: 'Allocation', color: theme.neutralLine, dash: true },
    ]} />
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartCard icon={Cpu} tint="text-violet-500" title="CPU — utilization vs allocation"
                 legend={utilLegend('#7c3aed')}>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={pts} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid {...theme.grid} vertical={false} />
            {xAxis}
            <YAxis {...theme.axis} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={40} />
            <Tooltip {...TOOLTIP} labelFormatter={labelFmt} cursor={theme.cursor}
                     formatter={(v, n) => [v == null ? '—' : `${Number(v).toFixed(1)}%`, n]} />
            <Line type="monotone" dataKey="cpu_utilization_pct" name="Utilization" stroke="#7c3aed" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="cpu_allocation_pct" name="Allocation" stroke={theme.neutralLine} strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard icon={HardDrive} tint="text-rose-500" title="Memory — utilization vs allocation"
                 legend={utilLegend('#e11d48')}>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={pts} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid {...theme.grid} vertical={false} />
            {xAxis}
            <YAxis {...theme.axis} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={40} />
            <Tooltip {...TOOLTIP} labelFormatter={labelFmt} cursor={theme.cursor}
                     formatter={(v, n) => [v == null ? '—' : `${Number(v).toFixed(1)}%`, n]} />
            <Line type="monotone" dataKey="mem_utilization_pct" name="Utilization" stroke="#e11d48" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="mem_allocation_pct" name="Allocation" stroke={theme.neutralLine} strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard icon={Server} tint="text-blue-500" title="Pods & nodes"
                 legend={<ChartLegend items={[
                   { label: 'Pods', color: '#3b82f6' },
                   { label: 'Nodes', color: '#0d9488' },
                 ]} />}>
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={pts} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="finopsPodsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid {...theme.grid} vertical={false} />
            {xAxis}
            <YAxis {...theme.axis} width={40} allowDecimals={false} />
            <Tooltip {...TOOLTIP} labelFormatter={labelFmt} cursor={theme.cursor} />
            <Area type="monotone" dataKey="pods" name="Pods" stroke="#3b82f6" strokeWidth={2} fill="url(#finopsPodsGrad)" dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="nodes" name="Nodes" stroke="#0d9488" strokeWidth={1.5} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
